import type { UserProfile } from './api';

export const SERVICE_ROLE = 'service';
export const AUTOPARK_ROLE = 'autopark';
export const CUSTOMER_ROLE = 'customer_individual';

export type ProfileMode = 'create' | 'edit';

export const MAX_PRIORITY_ORDER = 255;

export const WEEK_DAYS = [
  { name: 'mon', label: 'ПН' },
  { name: 'tue', label: 'ВТ' },
  { name: 'wed', label: 'СР' },
  { name: 'thu', label: 'ЧТ' },
  { name: 'fri', label: 'ПТ' },
  { name: 'sat', label: 'СБ' },
  { name: 'sun', label: 'ВС' },
];

export interface ScheduleValue {
  name: string;
  isActive: boolean;
  from: string;
  to: string;
}

/** Everything the create / edit forms edit, flattened to strings and booleans for controlled inputs. */
export interface ProfileValues {
  name: string;
  email: string;
  phone: string;
  contactPhone: string;
  roles: string[];
  statusId: string;
  partnerGroupId: string;
  password: string;
  passwordRepeat: string;
  generatePassword: boolean;
  balance: string;
  bonusBalance: string;
  comment: string;
  publicBalanceComment: string;
  bonusComment: string;
  publicBonusComment: string;
  loyaltyAmount: string;
  loyaltyComment: string;
  marketingText: string;
  acceptB2b: boolean;
  category: string;
  receiveType: string;
  classification: string;
  rate: string;
  discountLevelMode: string;
  discountLevel: string;
  serviceDiscountId: string;
  serviceDiscountPercent: string;
  countryId: string;
  regionId: string;
  cityId: string;
  /** Display label for city autocomplete (not sent to API). */
  cityName: string;
  address: string;
  lat: string;
  long: string;
  schedule: ScheduleValue[];
  contactPerson: string;
  contactPersonPhone: string;
  isPriority: boolean;
  priorityOrder: string;
  isFranchise: boolean;
  isCostWorkDumpingOff: boolean;
  canWorkWithAutopark: boolean;
  payOnline: boolean;
  emailNotifications: boolean;
  tgEnabled: boolean;
  paymentMethod: string;
  contractNumber: string;
  contractDate: string;
}

export interface ProfilePermissions {
  canEditDumpingOff: boolean;
  canEditContactPhone: boolean;
  canChangeClassification: boolean;
  canEditWorkWithAutopark: boolean;
  canChangePayOnline: boolean;
}

const emptySchedule = (): ScheduleValue[] =>
  WEEK_DAYS.map(({ name }) => ({ name, isActive: false, from: '', to: '' }));

export const emptyProfileValues = (): ProfileValues => ({
  name: '',
  email: '',
  phone: '',
  contactPhone: '',
  roles: [],
  statusId: '',
  partnerGroupId: '',
  password: '',
  passwordRepeat: '',
  generatePassword: false,
  balance: '',
  bonusBalance: '',
  comment: '',
  publicBalanceComment: '',
  bonusComment: '',
  publicBonusComment: '',
  loyaltyAmount: '',
  loyaltyComment: '',
  marketingText: '',
  acceptB2b: false,
  category: '2',
  receiveType: '3',
  classification: '',
  rate: '',
  discountLevelMode: '0',
  discountLevel: '1',
  serviceDiscountId: '',
  serviceDiscountPercent: '',
  countryId: '',
  regionId: '',
  cityId: '',
  cityName: '',
  address: '',
  lat: '',
  long: '',
  schedule: emptySchedule(),
  contactPerson: '',
  contactPersonPhone: '',
  isPriority: false,
  priorityOrder: '',
  isFranchise: false,
  isCostWorkDumpingOff: false,
  canWorkWithAutopark: false,
  payOnline: false,
  emailNotifications: false,
  tgEnabled: false,
  paymentMethod: '',
  contractNumber: '',
  contractDate: '',
});

export const isServiceRole = (roles: string[]) => roles[0] === SERVICE_ROLE;
export const isCustomerRole = (roles: string[]) => roles[0] === CUSTOMER_ROLE;
export const hasAutoparkRole = (roles: string[]) => roles.includes(AUTOPARK_ROLE);

/** Legacy `getPermissions`: what the logged-in admin may change on a profile. */
export const getProfilePermissions = (adminRoles: Record<string, unknown>): ProfilePermissions => {
  const has = (...roles: string[]) => roles.some((role) => role in adminRoles);
  return {
    canEditDumpingOff: has('sales-manager', 'sales-director', 'admin'),
    canEditContactPhone: has('sales-director', 'callcenter-supervisor', 'admin'),
    canChangeClassification: has('admin', 'sales-director'),
    canEditWorkWithAutopark: has('admin', 'b2b-manager'),
    canChangePayOnline: has('admin'),
  };
};

const text = (value: unknown): string => (value === null || value === undefined ? '' : String(value));
const flag = (value: unknown) => Boolean(Number(value));

const classificationValue = (value: UserProfile['classification']): string => {
  if (value && typeof value === 'object') return text(value.value ?? value.stars);
  return text(value);
};

const unixToDateInput = (seconds: unknown): string => {
  const value = Number(seconds);
  return value > 0 ? new Date(value * 1000).toISOString().slice(0, 10) : '';
};

const scheduleFromUser = (list: UserProfile['schedule_adv']): ScheduleValue[] =>
  emptySchedule().map((day) => {
    const found = list?.find((item) => item.name === day.name);
    return found
      ? { name: day.name, isActive: flag(found.is_active), from: text(found.from), to: text(found.to) }
      : day;
  });

export const profileToValues = (user: UserProfile): ProfileValues => ({
  ...emptyProfileValues(),
  name: text(user.name),
  email: text(user.email),
  phone: text(user.phone),
  contactPhone: text(user.contact_phone),
  roles: (user.roles ?? []).map((role) => role.value),
  statusId: text(user.status_id),
  partnerGroupId: text(user.group_partner?.id),
  balance: text(user.balance),
  bonusBalance: text(user.bonus_balance),
  marketingText: text(user.marketing_text),
  acceptB2b: flag(user.accept_b2b),
  category: text(user.category),
  receiveType: text(user.receive_type),
  classification: classificationValue(user.classification),
  rate: text(user.rate),
  discountLevelMode: text(user.discount_level_mode) || '0',
  discountLevel: text(user.discount_level?.level) || '1',
  serviceDiscountId: text(user.service_discount?.discount_id),
  serviceDiscountPercent: text(user.service_discount?.discount_percent),
  countryId: text(user.country?.id),
  regionId: text(user.region?.id),
  cityId: text(user.city?.id),
  cityName: text(user.city?.name),
  address: text(user.address),
  lat: text(user.lat),
  long: text(user.long),
  schedule: scheduleFromUser(user.schedule_adv),
  contactPerson: text(user.contact_person),
  contactPersonPhone: text(user.contact_person_phone),
  isPriority: flag(user.is_priority),
  priorityOrder: text(user.priority_order),
  isFranchise: flag(user.is_franchise),
  isCostWorkDumpingOff: flag(user.is_cost_work_dumping_off),
  canWorkWithAutopark: flag(user.can_work_with_autopark),
  payOnline: flag(user.pay_online),
  emailNotifications: flag(user.email_notifications_enabled),
  tgEnabled: flag(user.tg_enabled),
  paymentMethod: text(user.payment_method?.type),
  contractNumber: text(user.contract_number),
  contractDate: unixToDateInput(user.contract_time),
});

/** A loyalty type is picked (not "Выкл") — the percent is then mandatory. */
export const isServiceDiscountOn = (values: ProfileValues) => Number(values.serviceDiscountId) > 0;

const flagValue = (value: boolean) => (value ? '1' : '0');

/** Date input (`yyyy-mm-dd`) -> unix seconds string, as the API expects `contract_time`. */
const dateInputToUnix = (value: string) => String(Math.floor(new Date(value).getTime() / 1000));

const appendNested = (body: FormData, value: unknown, prefix: string) => {
  if (value === null || value === undefined) return;
  if (typeof value === 'object') {
    Object.entries(value).forEach(([key, item]) => appendNested(body, item, `${prefix}[${key}]`));
    return;
  }
  body.append(prefix, String(value));
};

/** `seo_data` arrives either as an object or as a JSON string. */
const parseSeoData = (raw: unknown): unknown => {
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/** FormData writer; `setFilled` skips empty values (legacy create: only truthy fields are sent). */
const createBodyWriter = () => {
  const body = new FormData();
  return {
    body,
    set: (key: string, value: string) => body.append(key, value),
    setFilled: (key: string, value: string) => {
      if (value) body.append(key, value);
    },
  };
};

export const buildCreateBody = (values: ProfileValues): FormData => {
  const { body, set, setFilled } = createBodyWriter();
  const isService = isServiceRole(values.roles);

  setFilled('email', values.email);
  setFilled('name', values.name);
  setFilled('phone', values.phone);
  setFilled('password', values.password);
  setFilled('password_repeat', values.passwordRepeat);
  setFilled('generate_password', values.generatePassword ? '1' : '');
  setFilled('balance', values.balance);
  setFilled('accept_b2b', values.acceptB2b ? '1' : '');
  setFilled('marketing_text', values.marketingText);
  values.roles.forEach((role) => body.append('roles[]', role));
  set('status_id', values.statusId);
  set('country_id', values.countryId);
  set('city_id', values.cityId);

  if (isService) {
    setFilled('receive_type', values.receiveType);
    setFilled('category', values.category);
  } else {
    set('discount_level_mode', values.discountLevelMode === '1' ? '1' : '0');
    if (values.discountLevelMode === '1') set('discount_level', values.discountLevel);
  }

  if (hasAutoparkRole(values.roles)) {
    setFilled('payment_method', values.paymentMethod);
    setFilled('contract_number', values.contractNumber);
    if (values.contractDate) set('contract_time', dateInputToUnix(values.contractDate));
  }

  return body;
};

export const buildEditBody = (
  userId: string | number,
  values: ProfileValues,
  permissions: ProfilePermissions,
  seoData: unknown,
): FormData => {
  const { body, set, setFilled } = createBodyWriter();
  const isService = isServiceRole(values.roles);

  set('user_id', String(userId));
  set('email', values.email);
  set('name', values.name);
  set('phone', values.phone);
  set('contact_phone', values.contactPhone);
  if (values.password) {
    set('password', values.password);
    set('password_repeat', values.passwordRepeat);
  }
  set('generate_password', flagValue(values.generatePassword));
  set('balance', values.balance);
  set('comment', values.comment);
  set('rate', values.rate);
  set('public_balance_comment', values.publicBalanceComment);
  set('bonus_balance', values.bonusBalance);
  set('marketing_text', values.marketingText);
  set('accept_b2b', flagValue(values.acceptB2b));
  set('bonus_comment', values.bonusComment);
  set('public_bonus_comment', values.publicBonusComment);
  set('is_franchise', flagValue(values.isFranchise));
  set('pay_online', flagValue(values.payOnline));
  set('is_priority', flagValue(values.isPriority));
  if (values.isPriority) set('priority_order', values.priorityOrder);
  set('email_notifications_enabled', flagValue(values.emailNotifications));
  set('tg_enabled', flagValue(values.tgEnabled));
  set('receive_type', values.receiveType);
  setFilled('city_id', values.cityId);
  setFilled('address', values.address);
  setFilled('lat', values.lat);
  setFilled('long', values.long);
  setFilled('contact_person', values.contactPerson);
  setFilled('contact_person_phone', values.contactPersonPhone);

  if (isServiceDiscountOn(values) && values.serviceDiscountPercent) {
    set('discount_id', values.serviceDiscountId);
    set('discount_percent', values.serviceDiscountPercent);
  } else {
    set('discount_percent', '0');
  }

  set('discount_level_mode', values.discountLevelMode === '1' ? '1' : '0');
  if (values.discountLevelMode === '1') set('discount_level', values.discountLevel);

  if (isService) {
    setFilled('category', values.category);
    appendNested(body, parseSeoData(seoData), 'seo_data');
  }
  if (permissions.canEditWorkWithAutopark) set('can_work_with_autopark', flagValue(values.canWorkWithAutopark));
  if (permissions.canChangeClassification) setFilled('classification', values.classification);
  if (permissions.canEditDumpingOff) set('is_cost_work_dumping_off', flagValue(values.isCostWorkDumpingOff));

  if (hasAutoparkRole(values.roles)) {
    setFilled('payment_method', values.paymentMethod);
    setFilled('contract_number', values.contractNumber);
    if (values.contractDate) set('contract_time', dateInputToUnix(values.contractDate));
  }

  values.roles.forEach((role) => body.append('roles[]', role));
  set('status_id', values.statusId);

  values.schedule.forEach(({ name, isActive, from, to }) => {
    const prefix = `schedule_adv[${name}]`;
    set(`${prefix}[is_active]`, flagValue(isActive));
    if (isActive) {
      set(`${prefix}[from]`, from.replace(':', '-'));
      set(`${prefix}[to]`, to.replace(':', '-'));
    }
  });

  return body;
};
